import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, ExternalLink, Trash2, Plus, MoveUp, MoveDown, Eye } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const BLOCK_LIBRARY = [
  { type: "hero", label: "Hero" },
  { type: "text", label: "Text" },
  { type: "cta", label: "CTA" },
  { type: "form", label: "Form" },
  { type: "image", label: "Image" },
];

function newBlock(type) {
  const props = {
    hero: { headline: "Big bold headline", subheadline: "A subheadline that explains the offer." },
    text: { text: "A paragraph of body copy." },
    cta: { text: "Book a demo", href: "#form" },
    form: { title: "Get in touch", cta: "Submit" },
    image: { url: "https://images.unsplash.com/photo-1488972685288-c3fd157d7c7a?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80", alt: "" },
  }[type] || {};
  return { id: crypto.randomUUID(), type, props };
}

export default function FunnelEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [funnel, setFunnel] = useState(null);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/funnels/${id}`);
      setFunnel(data);
      setSelected(data.blocks?.[0]?.id || null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      navigate("/funnels");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!funnel) return <div className="p-10 section-label">Loading…</div>;

  const updateBlock = (bid, patch) => {
    setFunnel({
      ...funnel,
      blocks: funnel.blocks.map((b) => b.id === bid ? { ...b, props: { ...b.props, ...patch } } : b),
    });
  };

  const move = (bid, dir) => {
    const idx = funnel.blocks.findIndex((b) => b.id === bid);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= funnel.blocks.length) return;
    const arr = [...funnel.blocks];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setFunnel({ ...funnel, blocks: arr });
  };

  const remove = (bid) => {
    setFunnel({ ...funnel, blocks: funnel.blocks.filter((b) => b.id !== bid) });
    if (selected === bid) setSelected(null);
  };

  const add = (type) => {
    const b = newBlock(type);
    setFunnel({ ...funnel, blocks: [...funnel.blocks, b] });
    setSelected(b.id);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/funnels/${id}`, {
        name: funnel.name,
        slug: funnel.slug,
        published: funnel.published,
        blocks: funnel.blocks,
      });
      setFunnel(data);
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const selectedBlock = funnel.blocks.find((b) => b.id === selected);
  const publicUrl = `${window.location.origin}/p/${funnel.slug}`;

  return (
    <div className="p-6 md:p-8" data-testid="funnel-editor-page">
      <Link to="/funnels" data-testid="back-funnels" className="section-label inline-flex items-center gap-1 hover:text-[var(--peak-accent)]"><ArrowLeft className="w-3 h-3" /> Back to funnels</Link>
      <div className="flex items-end justify-between mt-2 mb-6 flex-wrap gap-4">
        <div>
          <div className="section-label">Editing funnel</div>
          <input
            data-testid="funnel-title"
            value={funnel.name}
            onChange={(e) => setFunnel({ ...funnel, name: e.target.value })}
            className="brutalist-input font-heading font-black text-3xl md:text-4xl tracking-tighter uppercase mt-1"
          />
          <div className="text-xs text-[var(--peak-muted)] mt-1 font-mono">/p/{funnel.slug}</div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Switch data-testid="publish-toggle" checked={funnel.published} onCheckedChange={(v) => setFunnel({ ...funnel, published: v })} />
            <span className="section-label">{funnel.published ? "Published" : "Draft"}</span>
          </div>
          {funnel.published && (
            <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="preview-funnel" className="border-2 border-[var(--peak-ink)] hover:bg-black hover:text-white font-bold uppercase text-xs tracking-widest px-3 py-2 inline-flex items-center gap-1">
              <Eye className="w-4 h-4" /> Preview <ExternalLink className="w-3 h-3" />
            </a>
          )}
          <button data-testid="save-funnel" onClick={save} disabled={saving} className="bg-[var(--peak-accent)] hover:bg-black text-white font-bold uppercase text-xs tracking-widest px-4 py-2 inline-flex items-center gap-2 disabled:opacity-60">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Left rail: block library + block list */}
        <div className="col-span-12 md:col-span-3">
          <div className="brutalist-card p-4">
            <div className="section-label mb-3">Add block</div>
            <div className="grid grid-cols-2 gap-2">
              {BLOCK_LIBRARY.map((b) => (
                <button
                  key={b.type}
                  data-testid={`add-block-${b.type}`}
                  onClick={() => add(b.type)}
                  className="border border-[var(--peak-border)] hover:border-[var(--peak-ink)] p-2 text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-1"
                >
                  <Plus className="w-3 h-3" /> {b.label}
                </button>
              ))}
            </div>
          </div>
          <div className="brutalist-card p-4 mt-3">
            <div className="section-label mb-3">Blocks ({funnel.blocks.length})</div>
            <div className="space-y-1">
              {funnel.blocks.map((b) => (
                <button
                  key={b.id}
                  data-testid={`block-item-${b.id}`}
                  onClick={() => setSelected(b.id)}
                  className={`w-full text-left px-2 py-2 border text-xs font-bold uppercase tracking-widest ${selected === b.id ? "bg-[var(--peak-ink)] text-white border-[var(--peak-ink)]" : "border-[var(--peak-border)] hover:border-[var(--peak-ink)]"}`}
                >
                  {b.type}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Canvas */}
        <div className="col-span-12 md:col-span-6 brutalist-card p-6 min-h-[500px]" data-testid="funnel-canvas">
          {funnel.blocks.map((b) => (
            <BlockPreview key={b.id} block={b} onClick={() => setSelected(b.id)} selected={selected === b.id} />
          ))}
          {funnel.blocks.length === 0 && <div className="text-sm text-[var(--peak-muted)]">Add blocks from the left panel.</div>}
        </div>

        {/* Right: inspector */}
        <div className="col-span-12 md:col-span-3">
          {selectedBlock ? (
            <div className="brutalist-card p-4" data-testid="block-inspector">
              <div className="flex items-center justify-between mb-3">
                <div className="section-label">Block · {selectedBlock.type}</div>
                <div className="flex items-center gap-1">
                  <button onClick={() => move(selectedBlock.id, -1)} className="p-1 border border-[var(--peak-border)] hover:border-[var(--peak-ink)]"><MoveUp className="w-3 h-3" /></button>
                  <button onClick={() => move(selectedBlock.id, 1)} className="p-1 border border-[var(--peak-border)] hover:border-[var(--peak-ink)]"><MoveDown className="w-3 h-3" /></button>
                  <button onClick={() => remove(selectedBlock.id)} className="p-1 border border-[var(--peak-border)] hover:text-[#E50000]"><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
              <BlockEditor block={selectedBlock} onChange={(patch) => updateBlock(selectedBlock.id, patch)} />
            </div>
          ) : (
            <div className="brutalist-card p-4 text-sm text-[var(--peak-muted)]">Select a block to edit.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function BlockPreview({ block, onClick, selected }) {
  return (
    <div
      onClick={onClick}
      className={`mb-3 p-4 cursor-pointer border ${selected ? "border-[var(--peak-accent)]" : "border-[var(--peak-border)] hover:border-[var(--peak-ink)]"}`}
      data-testid={`preview-${block.type}`}
    >
      <RenderBlock block={block} />
    </div>
  );
}

export function RenderBlock({ block }) {
  const p = block.props || {};
  if (block.type === "hero") return (
    <div>
      <h1 className="font-heading font-black text-3xl md:text-5xl tracking-tighter uppercase">{p.headline || "Headline"}</h1>
      <p className="text-[var(--peak-muted)] mt-2">{p.subheadline || ""}</p>
    </div>
  );
  if (block.type === "text") return <p className="text-sm">{p.text}</p>;
  if (block.type === "cta") return (
    <a href={p.href || "#"} className="inline-block bg-[var(--peak-accent)] text-white font-bold uppercase tracking-widest px-6 py-3">{p.text || "Click"}</a>
  );
  if (block.type === "form") return (
    <div className="border border-[var(--peak-ink)] p-4">
      <div className="font-heading font-bold text-lg uppercase">{p.title || "Form"}</div>
      <div className="section-label">Name · Email · Message · [{p.cta || "Submit"}]</div>
    </div>
  );
  if (block.type === "image") return <img src={p.url} alt={p.alt || ""} className="w-full h-40 object-cover" />;
  return null;
}

function BlockEditor({ block, onChange }) {
  const p = block.props || {};
  const Field = ({ k, label, type = "text" }) => (
    <div className="mb-3">
      <label className="section-label">{label}</label>
      {type === "textarea" ? (
        <textarea rows={3} data-testid={`inspector-${block.type}-${k}`} className="w-full border border-[var(--peak-border)] p-2 text-sm focus:outline-none focus:border-[var(--peak-ink)]" value={p[k] || ""} onChange={(e) => onChange({ [k]: e.target.value })} />
      ) : (
        <input data-testid={`inspector-${block.type}-${k}`} className="brutalist-input" value={p[k] || ""} onChange={(e) => onChange({ [k]: e.target.value })} />
      )}
    </div>
  );
  if (block.type === "hero") return <>{Field({ k: "headline", label: "Headline" })}{Field({ k: "subheadline", label: "Subheadline", type: "textarea" })}</>;
  if (block.type === "text") return Field({ k: "text", label: "Text", type: "textarea" });
  if (block.type === "cta") return <>{Field({ k: "text", label: "Button label" })}{Field({ k: "href", label: "Link href" })}</>;
  if (block.type === "form") return <>{Field({ k: "title", label: "Title" })}{Field({ k: "cta", label: "Submit label" })}</>;
  if (block.type === "image") return <>{Field({ k: "url", label: "Image URL" })}{Field({ k: "alt", label: "Alt text" })}</>;
  return null;
}
